import { PrimaryButton } from '@stax-core/truffle-components';
import {
  FunctionComponent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Prompt } from 'react-router-dom';
import { coreapi } from '../../api';
import { Form, Group, PageHeader, ResponsivePadding } from '../../components';
import {
  AuthStore,
  BrandStore,
  RegistrationStore,
  updateAuthMerchant,
  updateRegistration,
} from '../../context';
import { history } from '../../history';
import {
  useAuthToken,
  useNextApplicationPath,
  useRedirectToNextApplicationPathIfPageIsHidden,
  useToaster,
  useWriteableFieldFormValidation,
} from '../../hooks';
import { FinancialFields } from './FinancialFields';
import { FinancialFieldsValues } from './FinancialFields.types';
import {
  formMutators,
  mapFormValuesToPayload,
  mapFormValuesToTeamPayload,
  mapRegistrationToInitialFormValues,
  validateFormValues,
} from './FinancialFields.util';

export const FinancialPage: FunctionComponent = () => {
  const { authToken } = useAuthToken();
  const { toaster, toast } = useToaster();
  const nextPath = useNextApplicationPath();
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { dispatch: authDispatch } = useContext(AuthStore);
  const {
    state: { brand },
  } = useContext(BrandStore);

  const { regSignerHeader, regSignerBody } = brand || {};
  const [disableFormSubmission, setDisableFormSubmission] = useState(false);

  const onSubmit = useCallback(
    async (formValues: FinancialFieldsValues) => {
      const payload = mapFormValuesToPayload(formValues);

      const teamPayload = mapFormValuesToTeamPayload(formValues);
      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));

        const m = await coreapi.put(authToken!, '/team', teamPayload);
        authDispatch(updateAuthMerchant(m));

        history.push(nextPath!);
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, authDispatch, dispatch, toaster, toast, nextPath],
  );

  const validate = useWriteableFieldFormValidation(validateFormValues);

  /**
   * This prevents UI bug where on screen resize all the fields are cleared.
   * We are not sure why this bug happens - Germ
   */
  const memoForm = useMemo(() => {
    return (
      <Form
        initialValues={mapRegistrationToInitialFormValues(registration)}
        validate={validate}
        mutators={formMutators}
        onSubmit={onSubmit}
      >
        {formProps => (
          <FinancialFields
            mode="edit"
            {...formProps}
            setDisableFormSubmission={setDisableFormSubmission}
          >
            <Prompt
              when={formProps.dirty && !formProps.submitting}
              message="You have unsaved changes; are you sure you want to leave without saving?"
            />
            <ResponsivePadding>
              <Group justify="end">
                <PrimaryButton
                  style={{ width: '100%' }}
                  type="submit"
                  id="financial-continue-button"
                  data-testid="financial-continue-button"
                  trackingId="financial-continue-button"
                  disabled={formProps.submitting || disableFormSubmission}
                >
                  {formProps.submitting ? 'Saving...' : 'Save & Continue'}
                </PrimaryButton>
              </Group>
            </ResponsivePadding>
          </FinancialFields>
        )}
      </Form>
    );
  }, [onSubmit, registration, validate, disableFormSubmission]);

  useRedirectToNextApplicationPathIfPageIsHidden('Business Owner/Financial');

  return (
    <>
      <PageHeader
        header={regSignerHeader}
        id="financial-page-header"
        data-testid="financial-page-header"
        description={<p>{regSignerBody}</p>}
      />
      {memoForm}
    </>
  );
};
